import { Autocomplete, Box, Stack, TextField } from '@mui/material';
import { fetchCreditLineEvaluations } from 'lib/api/decisionEngine';
import { EvaluationRow } from 'lib/api/decisionEngine/types';
import CustomButton from 'lib/designSystem/Button';
import PageHeader from 'lib/designSystem/PageHeader';
import EnhancedTable from 'lib/designSystem/Table';
import { validateCompanyTypes, validatePermissions } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import EvaluationDetails from './components/EvaluationDetails';
import NewEvaluation from './components/NewEvaluation';

const CreditHubProEvaluations: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [openNewEvaluation, setOpenNewEvaluation] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [evaluationId, setEvaluationId] = useState<number>();
  const [filteredNames, setFilteredNames] = useState<string[]>([]);
  const { data, isPending } = fetchCreditLineEvaluations(dispatch);

  const canViewDetails = useMemo(
    () => validatePermissions(user, 'creditHubPro_evaluation_view_details'),
    [user],
  );

  const canCreateEvaluation = useMemo(
    () => validatePermissions(user, 'creditHubPro_evaluation_create'),
    [user],
  );

  const businesses = useMemo(() => {
    return [...new Set(data?.map((x: EvaluationRow) => x.businessName))];
  }, [data]);

  const evaluations = useMemo(() => {
    if (filteredNames.length === 0) return data || [];
    return (
      data?.filter((x: EvaluationRow) =>
        filteredNames.includes(x.businessName),
      ) || []
    );
  }, [data, filteredNames]);

  const headers = useMemo(() => {
    const isFund = validateCompanyTypes(user, 'Fund');
    return [
      {
        key: 'business',
        label: 'Cliente',
        type: 'dual-text',
      },
      {
        key: 'engineName',
        label: 'Nombre motor',
      },
      { key: 'status', label: 'Resultado', type: 'status-chip' },
      { key: 'state', label: 'Estado', type: 'status-chip' },
      ...(isFund
        ? [{ key: 'origin', label: 'Origen', type: 'status-chip' }]
        : []),
      { key: 'createdAt', label: 'Fecha', type: 'date-large' },
    ];
  }, [user]);

  const handleDetails = (id: number) => {
    setEvaluationId(id);
    setOpenDetails(true);
  };

  const handleChange = (_: unknown, newValue: string | null) => {
    if (!newValue) return setFilteredNames([]);
    setFilteredNames([newValue]);
  };

  const handleNewEvaluation = () => {
    setOpenNewEvaluation(true);
  };

  return (
    <>
      <Box>
        <PageHeader.TitleOnly
          iconType="insights"
          title="Evaluaciones"
          subtitle="Aca podras revisar todas las evaluaciones que haz realizado.  "
        />
        <Stack direction="column" spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Autocomplete
              size="small"
              style={{ minWidth: 400 }}
              options={businesses}
              getOptionLabel={(option) => `${option}`}
              renderInput={(params) => (
                <TextField
                  style={{ minWidth: 250 }}
                  {...params}
                  label="Filtrar"
                />
              )}
              onChange={handleChange}
            />
            <CustomButton
              onClick={handleNewEvaluation}
              color="primary"
              disabled={!canCreateEvaluation}
            >
              Nueva evaluación
            </CustomButton>
          </Stack>
          <EnhancedTable
            dataHeaders={headers}
            data={evaluations}
            showActions
            handleClickRow={handleDetails}
            rowsPerPageDefault={25}
            exportToFile="xlsx"
            lastElement
            isLoading={isPending}
          />
        </Stack>
      </Box>
      {openNewEvaluation && (
        <NewEvaluation
          type="creditLine"
          open={openNewEvaluation}
          setOpen={setOpenNewEvaluation}
        />
      )}
      {openDetails && canViewDetails && (
        <EvaluationDetails
          evaluationId={evaluationId}
          open={openDetails}
          setOpen={setOpenDetails}
        />
      )}
    </>
  );
};

export default CreditHubProEvaluations;
